import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Select, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Activity, CircleCheckBig, Cpu, RefreshCw, TriangleAlert } from 'lucide-react';
import Layout from '../../Layout';
import { machineOptions } from './ktCards';
import { machineRows, teamText } from './ktCounter';
import { EXEC_CHECK, execRows, type ExecRow } from './ktExec';
import { dmy, type UsageEntry } from './ktLogic';
import { ktApi, today, useKt } from './ktStore';
import { DemoBar, Kpi, PageHeader, Panel } from './ktUi';

const PULL_EVERY_MS = 120_000;
const hm = (t?: number) => (t ? dayjs(t).format('HH:mm') : '');

/** Đối soát ca: người làm, phòng, giờ lấy từ khâu thực hiện để tính tua. Kế toán chỉ xem, không nhập */
export default function KeToanSessions() {
  const s = useKt();
  const now = today();
  const dates = useMemo(() => Array.from(new Set([now, ...s.bills.map(b => b.date)])).sort().reverse().slice(0, 30), [s.bills, now]);
  const [date, setDate] = useState(now);
  const months = useMemo(() => Array.from(new Set([now.slice(0, 7), ...s.bills.map(b => b.date.slice(0, 7))])).sort().reverse(), [s.bills, now]);
  const [ym, setYm] = useState(now.slice(0, 7));
  const [pulling, setPulling] = useState(false);
  const busy = useRef(false);

  const pull = async () => {
    if (busy.current) return;
    busy.current = true;
    setPulling(true);
    await ktApi.pullServerOrders();
    await ktApi.pullExecVisits();
    busy.current = false;
    setPulling(false);
  };
  useEffect(() => {
    pull();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') pull(); }, PULL_EVERY_MS);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => execRows(s, date), [s, date]);
  const usages = useMemo(() => s.usageLog.filter(e => e.date === date && !e.cancelled), [s.usageLog, date]);
  const machines = useMemo(() => machineRows(s, ym), [s, ym]);
  const count = (...c: ExecRow['check'][]) => rows.filter(r => c.includes(r.check)).length;

  const lineColumns: ColumnsType<ExecRow> = [
    { title: 'Bill', dataIndex: 'docNo', width: 100 },
    { title: 'Khách, dịch vụ', key: 'c', render: (_, r) => <div><div className="text-gray-900 dark:text-gray-100">{r.customer}</div><div className="text-[11px] text-gray-500">{r.service}{r.group ? ` · ${r.group}` : ''}</div></div> },
    { title: 'Người thực hiện', key: 't', render: (_, r) => <span className="text-xs">{teamText(r.team) || <span className="text-gray-400">Chưa có</span>}</span> },
    { title: 'Phòng', key: 'p', width: 130, render: (_, r) => <span className="text-xs">{r.exec?.room || <span className="text-gray-400">Chưa có</span>}</span> },
    { title: 'Bắt đầu, xong', key: 'g', width: 110, render: (_, r) => (r.exec?.at ? <span className="text-xs">{hm(r.exec.at)} → {hm(r.exec.doneAt) || '...'}</span> : <span className="text-xs text-gray-400">Chưa có</span>) },
    { title: 'Mã máy', key: 'm', width: 160, render: (_, r) => (r.machine ? <span className="text-xs">{r.machine}{r.shots ? ` · ${r.shots} shot` : ''}</span> : <span className="text-xs text-gray-400">{machineOptions([r.group]).length ? 'Thực hiện chưa ghi' : 'Không cần'}</span>) },
    { title: 'Đối soát', key: 's', width: 130, render: (_, r) => (
      <div>
        <Tag color={EXEC_CHECK[r.check].color} className="!mr-0">{EXEC_CHECK[r.check].label}</Tag>
        {r.exec && <div className="text-[11px] text-gray-500 mt-0.5">{r.exec.visitCode}{r.exec.by === 'phone' ? ' · theo SĐT' : ''}</div>}
      </div>
    ) },
  ];
  const usageColumns: ColumnsType<UsageEntry> = [
    { title: 'Số nhật ký', dataIndex: 'no', width: 110 },
    { title: 'Khách, dịch vụ', key: 'c', render: (_, e) => <div><div className="text-gray-900 dark:text-gray-100">{s.cards.find(c => c.id === e.cardId)?.customerName}</div><div className="text-[11px] text-gray-500">{e.items.map(i => `${i.name} ×${i.qty}`).join(', ')}</div></div> },
    { title: 'Người thực hiện', key: 't', render: (_, e) => <span className="text-xs">{teamText(e.team) || <span className="text-gray-400">Chưa có</span>}</span> },
    { title: 'Mã máy', key: 'm', width: 170, render: (_, e) => <span className="text-xs">{e.machine || 'Không có'}{e.shots ? ` · ${e.shots} shot` : ''}</span> },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Ca thực hiện" desc="Đối soát người làm, phòng và giờ từ khâu thực hiện. Kế toán chỉ xem, bảng tua lấy theo dữ liệu này."
          extra={<Select aria-label="Chọn ngày" value={date} onChange={setDate} className="w-56" options={dates.map(d => ({ value: d, label: `${dmy(d)}${d === now ? ' (hôm nay)' : ''}${s.locks[d] ? ' · đã khóa' : ''}` }))} />} />
        <DemoBar portal="counter" />
        {s.posSync.execError && <Alert type="error" showIcon message={`Không đọc được dữ liệu thực hiện: ${s.posSync.execError}`} />}
        <Alert type="info" showIcon message="Lễ tân ghi ekip, mã máy, số shot theo từng dịch vụ ở màn Hàng chờ. Phần chi tiết này đang lưu trên trình duyệt, nên Lễ tân và Kế toán phải dùng cùng một máy mới thấy nhau." />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi icon={<CircleCheckBig size={20} />} tone="bg-emerald-50 text-emerald-600" label="Dịch vụ đã xong" value={count('done', 'no_machine')} hint={`Trên ${rows.length} dịch vụ lẻ, ${count('no_machine')} thiếu mã máy`} />
          <Kpi icon={<Activity size={20} />} tone="bg-sky-50 text-sky-600" label="Đang làm" value={count('running')} hint="Đã xếp phòng, chưa báo xong" />
          <Kpi icon={<TriangleAlert size={20} />} tone="bg-amber-50 text-amber-600" label="Chưa có dữ liệu" value={count('none')} hint="Đơn chưa nối được với lượt thực hiện" />
        </div>

        <Panel title="Đối soát ca"
          extra={(
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{s.posSync.execAt ? `Đọc từ Lễ tân lúc ${dayjs(s.posSync.execAt).format('HH:mm')}` : 'Chưa đọc từ Lễ tân'}</span>
              <Button size="small" loading={pulling} icon={<RefreshCw size={13} />} onClick={pull}>Đồng bộ</Button>
            </div>
          )}>
          <Tabs className="px-4" items={[
            { key: 'lines', label: `Dịch vụ lẻ (${rows.length})`, children: <div className="pb-4"><Table rowKey="key" size="middle" columns={lineColumns} dataSource={rows} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 1000 }} locale={{ emptyText: 'Ngày này không có dịch vụ lẻ' }} /></div> },
            { key: 'usages', label: `Buổi liệu trình (${usages.length})`, children: (
              <div className="pb-4 space-y-2">
                <p className="text-xs text-gray-500 m-0">Lượt trừ buổi ghi ở màn Liệu trình, người làm lấy theo lượt đó.</p>
                <Table rowKey="id" size="middle" columns={usageColumns} dataSource={usages} pagination={false} scroll={{ x: 800 }} locale={{ emptyText: 'Ngày này không có lượt trừ thẻ' }} />
              </div>
            ) },
            { key: 'machines', label: 'Theo máy', children: (
              <div className="pb-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Select aria-label="Chọn tháng" value={ym} onChange={setYm} className="w-40" options={months.map(m => ({ value: m, label: dayjs(`${m}-01`).format('MM/YYYY') }))} />
                  <span className="text-xs text-gray-500">Số ca, số shot lấy từ phần Lễ tân ghi. Bút toán khấu hao vẫn lập tay ở màn Sổ, chờ Kế toán trưởng chốt đơn giá theo shot.</span>
                </div>
                <Table rowKey="code" size="middle" pagination={false} dataSource={machines.rows} columns={[
                  { title: 'Mã máy', dataIndex: 'code', width: 180 },
                  { title: 'Tên máy', dataIndex: 'name' },
                  { title: 'Nhóm dịch vụ', dataIndex: 'group', width: 150 },
                  { title: 'Số ca', dataIndex: 'sessions', width: 100, align: 'right' },
                  { title: 'Số shot đã ghi', dataIndex: 'shots', width: 130, align: 'right', render: (v: number) => (v ? new Intl.NumberFormat('vi-VN').format(v) : <span className="text-gray-400">Chưa ghi</span>) },
                ]} />
              </div>
            ) },
          ]} />
        </Panel>
      </div>
    </Layout>
  );
}
